import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { useData } from './DataContext';

const GamificationContext = createContext();

export const useGamification = () => {
  const context = useContext(GamificationContext);
  if (!context) {
    throw new Error('useGamification must be used within a GamificationProvider');
  }
  return context;
};

export const GamificationProvider = ({ children }) => {
  const { candidates, companies, hosts, candidateEvaluations, interviews } = useData();
  
  const [userPoints, setUserPoints] = useState(0);
  const [userLevel, setUserLevel] = useState(1);
  const [userBadges, setUserBadges] = useState([]);
  const [achievements, setAchievements] = useState([]);
  const [streak, setStreak] = useState(0);
  const [lastActivity, setLastActivity] = useState(null);

  // Points system configuration
  const pointsConfig = {
    profileCompletion: 50,
    cvUpload: 30,
    skillsAdded: 20,
    educationAdded: 20,
    interviewRequested: 10,
    interviewAttended: 50,
    interviewAccepted: 25,
    companyProfileComplete: 100,
    jobPosted: 40,
    interviewReviewed: 15,
    interviewScheduled: 25,
    candidateRated: 20,
    hostCompanyInvited: 50,
    hostEventCreated: 100,
    hostEventCompleted: 200,
    dailyLogin: 5,
    weeklyStreak: 50,
    monthlyStreak: 200,
  };

  // Badge definitions
  const badgeDefinitions = {
    firstProfile: { name: 'Profile Pioneer', description: 'Complete your profile', icon: '🎯', points: 50 },
    networker: { name: 'Networker', description: 'Request 5 interviews', icon: '🤝', points: 100 },
    superNetworker: { name: 'Super Networker', description: 'Request 20 interviews', icon: '🌟', points: 300 },
    interviewPro: { name: 'Interview Pro', description: 'Attend 10 interviews', icon: '💼', points: 200 },
    highlyRated: { name: 'Highly Rated', description: 'Average rating 4.5+', icon: '⭐', points: 150 },
    topEmployer: { name: 'Top Employer', description: 'Post 10 jobs', icon: '🏢', points: 200 },
    talentScout: { name: 'Talent Scout', description: 'Review 50 candidates', icon: '🔍', points: 300 },
    fairChampion: { name: 'Fair Champion', description: 'Conduct 30 interviews', icon: '🏆', points: 400 },
    communityBuilder: { name: 'Community Builder', description: 'Invite 10 companies', icon: '🏙️', points: 250 },
    eventMaster: { name: 'Event Master', description: 'Host 5 successful events', icon: '🎪', points: 500 },
    earlyBird: { name: 'Early Bird', description: 'Login before 9 AM', icon: '🌅', points: 25 },
    nightOwl: { name: 'Night Owl', description: 'Login after 9 PM', icon: '🦉', points: 25 },
    consistent: { name: 'Consistent', description: '7-day login streak', icon: '📅', points: 100 },
    dedicated: { name: 'Dedicated', description: '30-day login streak', icon: '💎', points: 500 },
  };

  const achievementDefinitions = {
    level5: { name: 'Rising Star', description: 'Reach level 5', points: 100 },
    level10: { name: 'Professional', description: 'Reach level 10', points: 250 },
    level25: { name: 'Expert', description: 'Reach level 25', points: 500 },
    level50: { name: 'Master', description: 'Reach level 50', points: 1000 },
    points100: { name: 'Point Getter', description: 'Earn 100 points', points: 50 },
    points500: { name: 'Point Collector', description: 'Earn 500 points', points: 200 },
    points1000: { name: 'Point Master', description: 'Earn 1000 points', points: 500 },
    points5000: { name: 'Point Legend', description: 'Earn 5000 points', points: 2000 },
  };

  const calculateLevel = (points) => {
    if (points < 100) return 1;
    if (points < 250) return 2;
    if (points < 500) return 3;
    if (points < 1000) return 4;
    if (points < 2000) return 5;
    if (points < 3500) return 6;
    if (points < 5000) return 7;
    if (points < 7500) return 8;
    if (points < 10000) return 9;
    if (points < 15000) return 10;
    return Math.floor(points / 2000) + 5;
  };

  const getPointsToNextLevel = (points) => {
    const currentLevel = calculateLevel(points);
    const levelThresholds = [0, 100, 250, 500, 1000, 2000, 3500, 5000, 7500, 10000, 15000];
    
    if (currentLevel >= levelThresholds.length) return null;
    
    const currentThreshold = levelThresholds[currentLevel - 1] || 0;
    const nextThreshold = levelThresholds[currentLevel] || Infinity;
    
    return {
      current: points - currentThreshold,
      needed: nextThreshold - currentThreshold,
      total: nextThreshold - currentThreshold
    };
  };

  const addPoints = (action, multiplier = 1) => {
    const points = (pointsConfig[action] || 0) * multiplier;
    const newPoints = userPoints + points;
    const newLevel = calculateLevel(newPoints);
    
    setUserPoints(newPoints);
    setUserLevel(newLevel);
    checkAchievements(newPoints, newLevel);
    return points;
  };

  const awardBadge = (badgeKey) => {
    const badge = badgeDefinitions[badgeKey];
    if (badge && !userBadges.find(b => b.key === badgeKey)) {
      const newBadge = { ...badge, key: badgeKey, earnedAt: new Date().toISOString() };
      setUserBadges(prev => [...prev, newBadge]);
      addPoints('badgeEarned', 1);
      return newBadge;
    }
    return null;
  };

  const checkAchievements = (points, level) => {
    Object.entries(achievementDefinitions).forEach(([key, achievement]) => {
      if (!achievements.find(a => a.key === key)) {
        let earned = false;
        if (key.startsWith('level') && level >= parseInt(key.replace('level', ''))) {
          earned = true;
        } else if (key.startsWith('points') && points >= parseInt(key.replace('points', ''))) {
          earned = true;
        }
        if (earned) {
          const newAchievement = { ...achievement, key, earnedAt: new Date().toISOString() };
          setAchievements(prev => [...prev, newAchievement]);
          addPoints('achievementEarned', 1);
        }
      }
    });
  };

  const updateStreak = () => {
    const today = new Date().toDateString();
    const lastActivityDate = lastActivity ? new Date(lastActivity).toDateString() : null;
    
    if (lastActivityDate === today) return;
    
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    
    if (lastActivityDate === yesterday.toDateString()) {
      setStreak(prev => prev + 1);
    } else {
      setStreak(1);
    }
    setLastActivity(new Date().toISOString());
    
    if (streak + 1 >= 7) awardBadge('consistent');
    if (streak + 1 >= 30) awardBadge('dedicated');
  };

  // Dynamic Leaderboard Calculation
  const leaderboard = useMemo(() => {
    // 1. Calculate Candidate Activity (Unique Company Scans/Evaluations)
    const candidateStats = candidates.map(candidate => {
      // Get all evaluations for this candidate
      const myEvaluations = candidateEvaluations.filter(e => e.candidateId === candidate.id);
      // Count unique company IDs from evaluations
      const uniqueCompanies = new Set(myEvaluations.map(e => e.companyId)).size;
      
      // Calculate a mock "points" score for ranking purposes if not persisting gamification data
      // In a real app, this would fetch the user's actual stored points
      const mockPoints = (uniqueCompanies * 100) + (candidate.profileComplete ? 200 : 0); 

      return {
        ...candidate,
        role: 'candidate',
        metricValue: uniqueCompanies,
        metricLabel: 'Unique Scans',
        points: mockPoints,
        level: calculateLevel(mockPoints),
        badges: userBadges.length // This would need to be per-user in real DB
      };
    });

    // 2. Calculate Company Activity (Completed Interviews & Evaluations)
    const companyStats = companies.map(company => {
      // Count interviews conducted by this company (completed)
      const completedInterviews = interviews.filter(i => i.companyId === company.id && i.status === 'completed').length;
      // Count evaluations submitted by this company
      const evaluationsSubmitted = candidateEvaluations.filter(e => e.companyId === company.id).length;
      
      // Combined metric for "Interviews & Evaluations"
      // Using evaluations count as the primary "quality" metric as per requirement
      const activityScore = evaluationsSubmitted;

      const mockPoints = (activityScore * 50) + (completedInterviews * 50);

      return {
        ...company,
        role: 'company',
        metricValue: activityScore,
        metricLabel: 'Evaluations',
        points: mockPoints,
        level: calculateLevel(mockPoints),
        badges: 5 // Mock
      };
    });

    // 3. Host Stats (Mock for now as logic wasn't explicitly changed)
    const hostStats = hosts.map(host => ({
      ...host,
      role: 'host',
      metricValue: companies.filter(c => c.hostId === host.id).length,
      metricLabel: 'Companies',
      points: 5000, // Mock
      level: 10,
      badges: 8
    }));

    // Combine and Sort
    const allEntries = [...candidateStats, ...companyStats, ...hostStats]
      .sort((a, b) => b.metricValue - a.metricValue) // Sort by the specific metric requested
      .map((entry, index) => ({
        ...entry,
        rank: index + 1
      }));

    return allEntries;
  }, [candidates, companies, hosts, candidateEvaluations, interviews, userBadges]);

  useEffect(() => {
    const savedProgress = localStorage.getItem('gamification_progress');
    if (savedProgress) {
      const progress = JSON.parse(savedProgress);
      setUserPoints(progress.points || 0);
      setUserLevel(progress.level || 1);
      setUserBadges(progress.badges || []);
      setAchievements(progress.achievements || []);
      setStreak(progress.streak || 0);
      setLastActivity(progress.lastActivity || null);
    }
    updateStreak();
    addPoints('dailyLogin');
    const hour = new Date().getHours();
    if (hour < 9) awardBadge('earlyBird');
    else if (hour >= 21) awardBadge('nightOwl');
  }, []);

  useEffect(() => {
    const progress = {
      points: userPoints,
      level: userLevel,
      badges: userBadges,
      achievements: achievements,
      streak: streak,
      lastActivity: lastActivity,
    };
    localStorage.setItem('gamification_progress', JSON.stringify(progress));
  }, [userPoints, userLevel, userBadges, achievements, streak, lastActivity]);

  const value = {
    userPoints,
    userLevel,
    userBadges,
    achievements,
    leaderboard,
    streak,
    pointsConfig,
    badgeDefinitions,
    achievementDefinitions,
    addPoints,
    awardBadge,
    getPointsToNextLevel,
    calculateLevel,
  };

  return (
    <GamificationContext.Provider value={value}>
      {children}
    </GamificationContext.Provider>
  );
};